// ============================================================================
// hits.js — resolves one of Benjen's attack hit-windows against the crowd.
// Uses the spatial hash for the broad phase (never O(n^2)); each candidate gets
// a precise arc test. Applies damage, reactions, FX, titan gain, hit-stop and
// camera shake, and handles officer blocking + guard-break.
// ============================================================================

import {
  TEAM_PLAYER, ST_DEAD, ST_BLOCK, UNIT_WARLORD, UNIT_CAPTAIN,
  HITSTOP_MIN, HITSTOP_MAX, HITSTOP_HEAVY, SHAKE_PER_HIT, SHAKE_MAX,
  TITAN_GAIN_DEAL, TITAN_GAIN_KILL, BLOCK_CHIP, BLOCK_ARC,
  RE_STUN,
} from '../core/constants.js';
import { inArc, angleDelta, headingTo } from '../core/math.js';
import { applyReaction, applyDeathImpulse } from './reactions.js';
import { addTitan } from './titan.js';
import { EV } from '../core/events.js';

/**
 * @param world
 * @param spec   a hit window from combos.js (radius, arc, dmg, reaction, knock, launch, heavy)
 * @param px,pz  attack origin (player position)
 * @param heading attack facing
 * @param token  unique token for this swing window (multi-hit guard)
 * @returns number of units struck this call
 */
export function resolveHit(world, spec, px, pz, heading, token) {
  const units = world.units;
  let hits = 0;
  let killedHeavy = false;

  world.hash.forEachInRadius(px, pz, spec.radius, (slot) => {
    const u = units.get(slot);
    if (!u._active || u.team === TEAM_PLAYER || u.state === ST_DEAD) return;
    if (u.hitToken === token) return;
    if (!inArc(px, pz, heading, u.x, u.z, spec.radius, spec.arc)) return;
    u.hitToken = token;

    let dmg = spec.dmg;
    let blocked = false;

    // ---- officer blocking: frontal guard soaks most of the hit ----
    if (u.state === ST_BLOCK && u.guardMax > 0) {
      const toAtk = headingTo(u.x, u.z, px, pz);
      if (Math.abs(angleDelta(u.heading, toAtk)) <= BLOCK_ARC * 0.5) {
        blocked = true;
        u.guard -= dmg;
        dmg *= BLOCK_CHIP;
        world.spawnSpark(u.x, 1.3, u.z, 0, 1, 0, 0.25, 1.4, 2 /* block spark */);
        if (u.guard <= 0) {
          // GUARD BROKEN → hard stun, wide-open
          u.guard = u.guardMax;
          applyReaction(world, u, RE_STUN, px, pz, 0, 0);
          world.spawnDmgNum(u.x, 2.2, u.z, 0, 2 /* guardbreak marker */);
        }
      }
    }

    u.hp -= dmg;
    u.flash = 1;
    addTitan(world, dmg * TITAN_GAIN_DEAL);

    // ember hit sparks
    const n = 3 + (spec.heavy ? 3 : 0);
    for (let i = 0; i < n; i++) {
      const a = world.rng() * 6.28, sp = 2 + world.rng() * 5;
      world.spawnSpark(
        u.x, 1.1 + world.rng() * 0.8, u.z,
        Math.cos(a) * sp, 2 + world.rng() * 4, Math.sin(a) * sp,
        0.3 + world.rng() * 0.3, 0.6 + world.rng() * 0.6, 0 /* ember */
      );
    }
    world.spawnDmgNum(u.x, 2.0 + world.rng() * 0.4, u.z, Math.round(dmg), spec.heavy ? 1 : 0);

    if (u.hp <= 0) {
      world.killUnit(u, true);
      applyDeathImpulse(u, px, pz, spec.knock + (spec.launch || 0) + 6);
      addTitan(world, TITAN_GAIN_KILL);
      if (u.type === UNIT_CAPTAIN || u.type === UNIT_WARLORD) killedHeavy = true;
      // captains sometimes drop a health cinder
      if ((u.type === UNIT_CAPTAIN || u.type === UNIT_WARLORD) && world.rng() < 0.9) {
        world.spawnPickup(u.x, u.z, 0 /* health */, u.type === UNIT_WARLORD ? 140 : 60);
      }
    } else if (!blocked) {
      applyReaction(world, u, spec.reaction, px, pz, spec.knock, spec.launch);
    }

    world.events.emit(EV.HIT, { x: u.x, z: u.z, damage: dmg, kill: u.hp <= 0, blocked });
    hits++;
  });

  if (hits > 0) {
    // combo counter
    world.combo += hits;
    world.comboTimer = 0;
    world.events.emit(EV.COMBO, { count: world.combo });
    // hit-stop scales with the weight of the blow and how deep the combo is
    let stop = spec.heavy ? HITSTOP_HEAVY : HITSTOP_MIN;
    stop = Math.min(HITSTOP_MAX + (spec.heavy ? 0.04 : 0), stop + Math.min(world.combo, 40) * 0.0008);
    if (killedHeavy) stop = Math.max(stop, HITSTOP_HEAVY + 0.05);
    world.hitstop = Math.max(world.hitstop, stop);
    // shake
    world.shake = Math.min(SHAKE_MAX, world.shake + SHAKE_PER_HIT * hits + (spec.heavy ? 0.15 : 0));
  }
  return hits;
}
